package com.evoapps.evoshift.ui.workplaces

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.MyLocation
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.Modifier
import com.evoapps.evoshift.domain.model.GeofenceMode
import com.evoapps.evoshift.domain.model.GeofenceSettings
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoRadioGroup
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.settings_geofence
import evoshift.composeapp.generated.resources.settings_geofence_block
import evoshift.composeapp.generated.resources.settings_geofence_block_hint
import evoshift.composeapp.generated.resources.settings_geofence_check
import evoshift.composeapp.generated.resources.settings_geofence_meters
import evoshift.composeapp.generated.resources.settings_geofence_needs_location
import evoshift.composeapp.generated.resources.settings_geofence_off
import evoshift.composeapp.generated.resources.settings_geofence_off_hint
import evoshift.composeapp.generated.resources.settings_geofence_radius
import evoshift.composeapp.generated.resources.settings_geofence_warn
import evoshift.composeapp.generated.resources.settings_geofence_warn_hint
import evoshift.composeapp.generated.resources.settings_help_geofence
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

const val GEOFENCE_DIALOG_TAG = "geofence_dialog"

/**
 * SH-106: the clock-in location check of a workplace: Off / Warn / Block and the radius. Warn and
 * Block need the workplace's location ([hasLocation]); without it only Off can be chosen.
 */
@Composable
fun GeofenceDialog(
    initial: GeofenceSettings,
    hasLocation: Boolean,
    onDismiss: () -> Unit,
    onSave: (GeofenceSettings) -> Unit
) {
    var mode by remember { mutableStateOf(if (hasLocation) initial.mode else GeofenceMode.OFF) }
    var radius by remember { mutableStateOf(GeofenceSettings.radiusOrDefault(initial.radiusMeters)) }
    EvoDialog(
        title = stringResource(Res.string.settings_geofence),
        icon = Icons.Default.MyLocation,
        onDismiss = onDismiss,
        modifier = Modifier.testTag(GEOFENCE_DIALOG_TAG),
        confirm = DialogAction(stringResource(Res.string.save), { onSave(GeofenceSettings(mode, radius)) })
    ) {
        Text(stringResource(Res.string.settings_help_geofence), color = Color.White.copy(alpha = 0.85f))
        if (!hasLocation) {
            Text(stringResource(Res.string.settings_geofence_needs_location), color = Color(0xFFFFB74D))
        }
        DialogSection(stringResource(Res.string.settings_geofence_check)) {
            EvoRadioGroup(
                options = if (hasLocation) GeofenceMode.entries else listOf(GeofenceMode.OFF),
                selected = mode,
                onSelect = { mode = it },
                optionLabel = { stringResource(geofenceModeTitleRes(it)) },
                description = { stringResource(geofenceModeHintRes(it)) }
            )
        }
        if (mode != GeofenceMode.OFF) {
            DialogSection(stringResource(Res.string.settings_geofence_radius)) {
                EvoRadioGroup(
                    options = (GeofenceSettings.RADIUS_CHOICES + radius).distinct().sorted(),
                    selected = radius,
                    onSelect = { radius = it },
                    optionLabel = { stringResource(Res.string.settings_geofence_meters, it) }
                )
            }
        }
    }
}

fun geofenceModeTitleRes(mode: GeofenceMode): StringResource = when (mode) {
    GeofenceMode.OFF -> Res.string.settings_geofence_off
    GeofenceMode.WARN -> Res.string.settings_geofence_warn
    GeofenceMode.BLOCK -> Res.string.settings_geofence_block
}

fun geofenceModeHintRes(mode: GeofenceMode): StringResource = when (mode) {
    GeofenceMode.OFF -> Res.string.settings_geofence_off_hint
    GeofenceMode.WARN -> Res.string.settings_geofence_warn_hint
    GeofenceMode.BLOCK -> Res.string.settings_geofence_block_hint
}
